import PageLink from '@/utils/PageLink';

import { CONNECTOR_HOSTS } from './connectors/connector-hosts';

/**
 * Single source of truth for the /docs catalogue.
 *
 * The hub page, the search index and the article cross-links all read from
 * here, so adding an article means editing one list instead of four. Keywords
 * are extra search terms that do not appear in the title or description
 * (product synonyms, the name of the screen in the app, competitor phrasing).
 */
export interface DocsCatalogArticle {
  title: string;
  description: string;
  href: string;
  readingTime: string;
  keywords?: string[];
  /** Marks articles added or substantially rewritten in the current release. */
  isNew?: boolean;
}

export interface DocsCatalogCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  articles: DocsCatalogArticle[];
}

export const DOCS_CATALOG: DocsCatalogCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    description: 'Create your first agent and give it something to know.',
    icon: 'tabler:rocket',
    articles: [
      {
        title: 'Your first AI agent in 10 minutes',
        description: 'Create a chatbot, train it on a document, and put it on your site.',
        href: PageLink.docsGettingStarted,
        readingTime: '10 min',
        keywords: ['quickstart', 'setup', 'new bot', 'onboarding', 'tutorial'],
      },
      {
        title: 'The knowledge upload playbook',
        description:
          'Which source type to use for which content, and how to keep a knowledge base clean.',
        href: PageLink.docsKnowledgeUpload,
        readingTime: '8 min',
        keywords: ['documents', 'links', 'crawl', 'text', 'q&a', 'audio', 'pdf', 'training'],
      },
      {
        title: 'Tuning your bot',
        description:
          'Model choice, system prompt, temperature, and the settings that actually move answer quality.',
        href: PageLink.docsTuneBot,
        readingTime: '9 min',
        keywords: ['model', 'prompt', 'temperature', 'persona', 'tune bot', 'gpt', 'claude'],
        isNew: true,
      },
      {
        title: '7 AI chatbot myths debunked',
        description: 'What building a grounded assistant actually requires — and what it does not.',
        href: PageLink.docsAiMyths,
        readingTime: '8 min',
        keywords: ['myths', 'hallucination', 'fine-tuning', 'cost'],
      },
    ],
  },
  {
    id: 'deploy',
    title: 'Deploy & connect',
    description: 'Get the agent in front of people, wherever they already are.',
    icon: 'tabler:plug-connected',
    articles: [
      {
        title: 'Website integration',
        description: 'Script widget, iframe embed, share link, and domain restrictions.',
        href: PageLink.docsWebsiteIntegration,
        readingTime: '9 min',
        keywords: ['embed', 'widget', 'script', 'iframe', 'wordpress', 'shopify', 'launcher'],
      },
      {
        title: 'Channel integrations',
        description: 'Slack, Microsoft Teams, Discord, Telegram, WhatsApp, Zendesk and Zoho Desk.',
        href: PageLink.docsChannelIntegrations,
        readingTime: '11 min',
        keywords: ['slack', 'teams', 'discord', 'telegram', 'whatsapp', 'zendesk', 'zoho'],
        isNew: true,
      },
      {
        title: 'Voice lines & appointments',
        description: 'Give a bot a phone number that answers calls and books appointments.',
        href: PageLink.docsVoiceLines,
        readingTime: '10 min',
        keywords: ['phone', 'voice', 'twilio', 'call', 'booking', 'appointments', 'minutes'],
        isNew: true,
      },
      {
        title: 'MCP management agent setup',
        description: 'Connect a local MCP client to manage chatbots, knowledge and analytics.',
        href: PageLink.docsMcpAgentSetup,
        readingTime: '10 min',
        keywords: ['mcp', 'claude desktop', 'cursor', 'model context protocol', 'connector'],
      },
    ],
  },
  {
    id: 'ai-apps',
    title: 'Experts in AI apps',
    description: 'Ask your Chat.co experts from Claude, ChatGPT, Cursor, Codex, Grok and more.',
    icon: 'tabler:apps',
    articles: [
      {
        title: 'Your experts in your AI apps',
        description:
          'How connections work, availability per app, sign-in, citations, credits and privacy.',
        href: PageLink.docsExternalExperts,
        readingTime: '6 min',
        keywords: [
          'experts',
          'connector',
          'mcp',
          'oauth',
          'citations',
          'connected applications',
          'claude',
          'chatgpt',
          'cursor',
          'codex',
          'grok',
        ],
        isNew: true,
      },
      ...CONNECTOR_HOSTS.map((host) => ({
        title: `Chat.co experts in ${host.name}`,
        description: `${host.status}. Covers ${host.surfaces}.`,
        href: host.href,
        readingTime: host.readingTime,
        keywords: [host.name.toLowerCase(), 'connector', 'mcp', 'experts', ...host.keywords],
        isNew: true,
      })),
    ],
  },
  {
    id: 'operate',
    title: 'Run it day to day',
    description: 'The screens you live in once the agent is answering real questions.',
    icon: 'tabler:adjustments',
    articles: [
      {
        title: 'Human handoff',
        description: 'Let an agent escalate to a person, and answer live from the dashboard.',
        href: PageLink.docsHumanHandoff,
        readingTime: '8 min',
        keywords: ['handoff', 'escalation', 'live chat', 'agent', 'takeover', 'support'],
        isNew: true,
      },
      {
        title: 'Lead capture',
        description: 'Collect contact details in the conversation and get them out to your CRM.',
        href: PageLink.docsLeadCapture,
        readingTime: '7 min',
        keywords: ['leads', 'forms', 'email capture', 'crm', 'export'],
        isNew: true,
      },
      {
        title: 'Analytics, ROI & knowledge gaps',
        description: 'Read the numbers, then turn unanswered questions into new knowledge.',
        href: PageLink.docsAnalyticsRoi,
        readingTime: '9 min',
        keywords: ['analytics', 'roi', 'metrics', 'knowledge gaps', 'reporting', 'usage'],
        isNew: true,
      },
      {
        title: 'Document Studio',
        description: 'Generate long-form documents grounded in a bot’s knowledge base.',
        href: PageLink.docsDocumentStudio,
        readingTime: '9 min',
        keywords: ['document generation', 'docx', 'reports', 'drafting', 'studio'],
        isNew: true,
      },
      {
        title: 'Managing multiple bots & teams',
        description: 'Separate bots by audience, and share them without sharing your password.',
        href: PageLink.docsMultiBotOrchestration,
        readingTime: '12 min',
        keywords: ['multi bot', 'organisation', 'workspace', 'scale'],
      },
      {
        title: 'Team, roles & seats',
        description: 'Admins, staff, per-bot permissions, and how seats are counted.',
        href: PageLink.docsTeamRoles,
        readingTime: '8 min',
        keywords: ['team', 'staff', 'admin', 'permissions', 'roles', 'seats', 'invite'],
        isNew: true,
      },
    ],
  },
  {
    id: 'build',
    title: 'Build with the API',
    description: 'Programmatic access to bots, knowledge and conversations.',
    icon: 'tabler:code',
    articles: [
      {
        title: 'API reference',
        description: 'Authentication, scopes, every REST endpoint, and streaming.',
        href: PageLink.docsApiReference,
        readingTime: '20 min',
        keywords: ['rest', 'api key', 'sk_live', 'pk_live', 'scopes', 'streaming', 'sse'],
      },
      {
        title: 'API vs MCP',
        description: 'Which integration path fits which job — and when to use both.',
        href: PageLink.docsApiVsMcp,
        readingTime: '9 min',
        keywords: ['comparison', 'integration', 'architecture', 'choose'],
      },
      {
        title: 'Build a full RAG system',
        description: 'End-to-end retrieval-augmented generation on top of Chat.co.',
        href: PageLink.docsBuildRagSystem,
        readingTime: '25 min',
        keywords: ['rag', 'retrieval', 'embeddings', 'chunking', 'vector', 'architecture'],
      },
      {
        title: 'Private-app chatbot with MCP',
        description: 'Turn an internal app into a trained chatbot in about 30 minutes.',
        href: PageLink.docsPrivateAppChatbotMcp,
        readingTime: '14 min',
        keywords: ['internal', 'private', 'mcp', 'connector', 'intranet'],
      },
      {
        title: 'FAQ to AI expert migration',
        description: 'Turn a static FAQ page into an assistant that actually answers.',
        href: PageLink.docsFaqMigration,
        readingTime: '15 min',
        keywords: ['faq', 'migration', 'help centre', 'knowledge base'],
      },
    ],
  },
  {
    id: 'trust',
    title: 'Security & quality',
    description: 'Keep answers grounded, and keep your data where it belongs.',
    icon: 'tabler:shield-check',
    articles: [
      {
        title: 'Citation quality',
        description: 'Why a citation is wrong, and the levers that fix it.',
        href: PageLink.docsCitationQuality,
        readingTime: '8 min',
        keywords: ['citations', 'sources', 'grounding', 'accuracy', 'hallucination'],
      },
      {
        title: 'Troubleshooting',
        description: 'Symptom-first fixes for the failures people actually hit.',
        href: PageLink.docsTroubleshooting,
        readingTime: '12 min',
        keywords: ['debug', 'errors', 'not working', 'failed', 'stuck', 'support'],
      },
      {
        title: 'Security & compliance',
        description: 'Data handling, model privacy posture, access control and retention.',
        href: PageLink.docsSecurityCompliance,
        readingTime: '11 min',
        keywords: [
          'security',
          'iso 27001',
          'iso/iec 27001:2022',
          'gcai-isms-lm73',
          'certification',
          'gdpr',
          'soc 2',
          'privacy',
          'encryption',
          'retention',
          'training',
        ],
      },
      {
        title: 'Enterprise SSO',
        description: 'Set up OIDC single sign-on and enforce it for your email domain.',
        href: PageLink.docsEnterpriseSso,
        readingTime: '9 min',
        keywords: ['sso', 'oidc', 'okta', 'entra', 'azure ad', 'google workspace', 'login'],
        isNew: true,
      },
    ],
  },
  {
    id: 'atlas',
    title: 'Atlas',
    description: 'Owner guides for access, reviewed actions, knowledge quality and recovery.',
    icon: 'tabler:compass',
    articles: [
      {
        title: 'Atlas owner guide',
        description: 'Find the right guide and understand what is available to your account.',
        href: PageLink.docsAtlas,
        readingTime: '3 min',
        keywords: ['atlas', 'owner', 'availability', 'support'],
      },
      {
        title: 'Getting started with Atlas',
        description: 'Check access, open your workspace and return to Classic.',
        href: PageLink.docsAtlasGettingStarted,
        readingTime: '4 min',
        keywords: ['atlas', 'enrollment', 'home', 'classic', 'permissions'],
      },
      {
        title: 'Review and Apply in Atlas',
        description: 'Review each exact effect, dismiss proposals and handle expired approvals.',
        href: PageLink.docsAtlasApprovals,
        readingTime: '5 min',
        keywords: ['atlas', 'apply', 'approval', 'dismiss', 'expiry', 'plan'],
      },
      {
        title: 'Atlas knowledge and answer quality',
        description: 'Prepare sources, refresh knowledge when available and assess test evidence.',
        href: PageLink.docsAtlasKnowledge,
        readingTime: '5 min',
        keywords: ['atlas', 'onboarding', 'refresh', 'golden tests', 'citations', 'quality'],
      },
      {
        title: 'Atlas roles, privacy and key delivery',
        description:
          'Understand account and bot permissions, private key delivery and safe sharing.',
        href: PageLink.docsAtlasPrivacy,
        readingTime: '4 min',
        keywords: ['atlas', 'roles', 'team', 'privacy', 'key', 'delivery'],
      },
      {
        title: 'Troubleshooting Atlas',
        description:
          'Handle interruptions, unknown outcomes, unavailable capabilities and usage limits.',
        href: PageLink.docsAtlasTroubleshooting,
        readingTime: '5 min',
        keywords: ['atlas', 'interrupted', 'unknown outcome', 'retry', 'limits', 'payment'],
      },
    ],
  },
];

/** Flat list used by the hub's client-side search. */
export const DOCS_SEARCH_INDEX: Array<DocsCatalogArticle & { category: string }> =
  DOCS_CATALOG.flatMap((category) =>
    category.articles.map((article) => ({ ...article, category: category.title }))
  );
